import React, { useEffect, useState } from 'react';
import { Button, Input, message, Typography, Upload } from 'antd';
import './detail-index.scss';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../store';
import { useNavigate, useParams } from 'react-router-dom';
import { InfoCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { createTrivia, findTriviaByCondition, resetDetail, updateTrivia } from '../../../../store/trivia/triviaSlice';

const { Text } = Typography;
const { TextArea } = Input;
const TriviaDetail: React.FC = () => {
  const triviaSlice = useSelector((state: RootState) => state.trivia);
  const { detail } = triviaSlice;
  const { id } = useParams<{ id: string }>();
  const [header, setHeader] = useState('');
  const [title, setTitle] = useState('');
  const [question, setQuestion] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [previewImage, setPreviewImage] = useState<any>('');
  const [imageUrl, setImageUrl] = useState('');
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      dispatch(findTriviaByCondition({ id: id }));
    }
    return () => {
      dispatch(resetDetail());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (detail && id) {
      setHeader(detail.header);
      setTitle(detail.title);
      setQuestion(detail.question);
      setDescription(detail.description);
      setContent(detail.content);
      setPreviewImage(detail.image ? `${process.env.REACT_APP_API_URL}${detail.image}` : '');
    }
  }, [id, detail]);

  const handleSave = async () => {
    if (!header.trim()) {
      message.error('ヘッダーを入力してください。');
      return;
    }
    if (!title.trim()) {
      message.error('タイトルを入力してください。');
      return;
    }
    if (!id && !imageUrl) {
      message.error('画像を選択してください。');
      return;
    }
    if (id && !previewImage && !imageUrl) {
      message.error('画像を選択してください。');
      return;
    }
    if (!question.trim()) {
      message.error('質問を入力してください。');
      return;
    }
    if (content.trim() === '') {
      message.error('内容を入力してください。');
      return;
    }
    if (description.trim() === '') {
      message.error('説明を入力してください。');
      return;
    }
    const formData = new FormData();
    formData.append('header', header);
    formData.append('title', title);
    formData.append('question', question);
    formData.append('content', content);
    formData.append('description', description);
    if (imageUrl) {
      formData.append('image', imageUrl);
    }
    if (id) {
      formData.append('id', id);
      const result = await dispatch(updateTrivia(formData));
      if (result.meta.requestStatus === 'fulfilled') {
        message.success('更新が成功しました!');
        navigate('/trivia-access');
      } else {
        message.error('更新に失敗しました。もう一度お試しください。');
      }
    } else {
      const result = await dispatch(createTrivia(formData));
      if (result.meta.requestStatus === 'fulfilled') {
        message.success('新規作成が成功しました!');
        navigate('/trivia-access');
      } else {
        message.error('新規作成に失敗しました。もう一度お試しください!');
      }
    }
  };
  const handleBack = () => {
    window.history.back();
  };

  const handleUploadChange = async ({ fileList: newFileList }) => {
    const latestFile = newFileList[newFileList.length - 1];
    if (latestFile && latestFile.originFileObj) {
      const objectUrl = URL.createObjectURL(latestFile.originFileObj);
      setPreviewImage(objectUrl);
      setImageUrl(latestFile.originFileObj);
    }
  };

  const resetForm = () => {
    setTitle('');
    setQuestion('');
    setDescription('');
    setPreviewImage('');
    setImageUrl('');
  };

  return (
    <div className="voucher-detail">
      <div className="header">
        <Button className="back-button" onClick={handleBack}>
          ←
        </Button>
        <Button className="save-button" type="primary" onClick={handleSave}>
          保存
        </Button>
      </div>
      <div className="section-title">
        Trivia Access
        <Button
          type="text"
          icon={
            <img src={`${process.env.PUBLIC_URL}/assets/icons/delete.png`} alt="Delete Logo" className="icon-table" />
          }
          danger
          onClick={() => resetForm()}
        />
      </div>
      <div className="form-container">
        <div className="form-group">
          <label>ヘッダ</label>
          <Input placeholder="コンテンツを入力" value={header} onChange={(e) => setHeader(e.target.value)} />
        </div>
        <div className="form-group">
          <label>タイトル</label>
          <Input placeholder="コンテンツを入力" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="form-group">
          <label>画像表示</label>
          <div className="image-upload-container">
            <div className="image-preview-wrapper">
              {previewImage ? (
                <>
                  <img src={previewImage} alt="uploaded" className="preview-img" />
                  <div className="image-upload-overlay">
                    <Upload
                      accept=".png,.jpg,.jpeg,.gif,.bmp,.webp,.svg"
                      name="image"
                      listType="picture"
                      maxCount={1}
                      onChange={handleUploadChange}
                      showUploadList={false}
                      beforeUpload={(file) => {
                        const isImage = file.type.startsWith('image/');
                        if (!isImage) {
                          message.error('画像ファイルのみアップロード可能です。');
                        }
                        const isLt2M = file.size / 1024 / 1024 < 2;
                        if (!isLt2M) {
                          message.error('画像サイズは2MB以下である必要があります。');
                        }
                        return false;
                      }}
                    >
                      <Button
                        className="upload-button overlay-button"
                        icon={<UploadOutlined className="upload-icon" />}
                      >
                        アップロード
                      </Button>
                    </Upload>
                  </div>
                </>
              ) : (
                <Upload
                  name="image"
                  listType="picture"
                  maxCount={1}
                  onChange={handleUploadChange}
                  showUploadList={false}
                  beforeUpload={(file) => {
                    const isImage = file.type.startsWith('image/');
                    if (!isImage) {
                      message.error('画像ファイルのみアップロード可能です。');
                    }
                    const isLt2M = file.size / 1024 / 1024 < 2;
                    if (!isLt2M) {
                      message.error('画像サイズは2MB以下である必要があります。');
                    }
                    return false;
                  }}
                >
                  <Button className="upload-button" icon={<UploadOutlined className="upload-icon" />}>
                    アップロード
                  </Button>
                </Upload>
              )}
            </div>
            <div className="info-text-container">
              <InfoCircleOutlined className="info-text-icon" />
              <Text className="info-text" type="secondary">
                最適に表示するために、サイズ（300×600px）の画像を選択してください
              </Text>
            </div>
          </div>
        </div>
        <div className="form-group">
          <label>質問</label>
          <Input placeholder="質問を入力してください" value={question} onChange={(e) => setQuestion(e.target.value)} />
        </div>
        <div className="form-group">
          <label>コンテンツ</label>
          <TextArea
            placeholder="コンテンツを入力"
            rows={4}
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>詳細</label>
          <TextArea
            placeholder="コンテンツを入力"
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </div>
    </div>
  );
};

export default TriviaDetail;
